import { UserAvatarStack } from "@cmp-src/components/UserAvatar/UserAvatarStack";
import { headingClasses } from "@cmp-src/data-display/Heading/Heading";
import { Image } from "@cmp-src/data-display/Image/Image";
import { useIsMobile } from "@cmp-src/hooks/use-mobile";
import { Popover } from "@cmp-src/overlay/Popover/Popover";
import { Badge } from "@cmp-src/primitives/badge";
import { Button } from "@cmp-src/primitives/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";
import { useTactic } from "@cmp-src/providers/TacticProvider/context";
import { IResource, ISchedule } from "@gettactic/api";
import { cn, formatStartEndSlotHours, useDisclosure } from "@gettactic/helpers";
import {
  gerCroppedImageUrl,
  getResourceCoverUrl
} from "@gettactic/helpers/src/resources/getResourceCoverUrl";
import { getScheduleAttendeeUsers } from "@gettactic/helpers/src/schedules/getScheduleAttendees";
import { truncateOfficeAndFloor } from "@gettactic/helpers/src/strings/truncateOfficeAndFloor";
import { useSaveResourceFavorite } from "@gettactic/hooks";
import { StarIcon } from "lucide-react";
import React from "react";
import { CapacityLabel } from "../CapacityLabel/CapacityLabel";

export interface ResourceLabelProps {
  resource: Partial<IResource>;
  className?: string;
  /** Shows the favorite star. Default true. */
  allowFavorite?: boolean;
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  showPicture?: boolean;
  headingClassName?: string;
  infoWrapperClassName?: string;
  hierarchyClassName?: string;
  /** Amenities shown before "+N". Default 2. */
  maxTags?: number;
  /** Shows the first booking's times, and how many more there are. */
  schedules?: ISchedule[];
  /** Shows the office and floor line. Default true. */
  showHierarchy?: boolean;
  showAmenities?: boolean;
  /** Content right of the name. */
  headerRight?: React.ReactNode | undefined;
  /** Content before the office and floor. */
  hierarchyLeft?: React.ReactNode | undefined;
  /** Content right of the office and floor. */
  hierarchyRight?: React.ReactNode | undefined;
  /** With `schedules`, shows who is coming. */
  showAttenders?: boolean;
  /** Size of the picture: 73px by default, "md" 57px, "sm" 40px. */
  imageSize?: "default" | "sm" | "md";
  /** Extra content rendered at the bottom of the info column (e.g. a status line). */
  footer?: React.ReactNode;
}

export const classes = {
  Wrapper: "group flex items-start gap-4",
  InfoSide: "flex flex-1 items-center justify-between min-w-0",
  ImageWrapper:
    "rounded-[5px] overflow-hidden bg-neutral-50 h-[73px] w-[73px] md:h-[70px] md:w-[70px] flex-shrink-0",
  Image: "h-[73px] md:h-[74px] object-cover",
  InfoWrapper: "flex w-full flex-1 flex-col gap-1.5 items-start min-w-0",
  ResourceLocation:
    "text-[13px] text-content flex items-center gap-1 capitalize font-inter min-w-0 overflow-hidden",
  ResourceName: "truncate font-inter font-bold",
  ResourceTagsWrapper: "text-[12px] text-content flex gap-1 justify-start",
  FavoriteWrapper:
    "self-start opacity-0 transition-opacity group-hover:opacity-100 shrink-0",
  FavoriteWrapperActive: "self-start transition-opacity shrink-0",
  FavoriteWrapperMobile: "self-start opacity-100 transition-opacity shrink-0"
};

/**
 * A bookable place: photo, name, office and floor, capacity, amenities and a favorite
 * star. Reads the office and floor names from `TacticProvider`.
 */
export const ResourceLabel: React.FC<ResourceLabelProps> = ({
  resource,
  className,
  allowFavorite = true,
  onMouseLeave,
  onMouseEnter,
  onClick,
  showPicture = true,
  headingClassName,
  infoWrapperClassName,
  hierarchyClassName,
  maxTags = 2,
  schedules,
  showHierarchy = true,
  showAmenities = true,
  headerRight,
  hierarchyLeft,
  hierarchyRight,
  showAttenders = false,
  imageSize = "default",
  footer
}) => {
  const { divisionCategories } = useTactic();
  const { toggleResourceFavorite, isLoading: isFavoriteLoading } =
    useSaveResourceFavorite();

  const amenities = React.useMemo(() => {
    if (!showAmenities || !resource?.tags || resource.tags.length === 0)
      return [];
    return resource.tags.filter((tag) => tag.type === "amenity");
  }, [resource?.tags, maxTags, showAmenities]);

  const tagsToShow = React.useMemo(() => {
    if (!amenities || amenities.length === 0) return [];
    return amenities.slice(0, maxTags);
  }, [amenities]);

  const office = resource.office_id
    ? divisionCategories.offices[resource.office_id]
    : undefined;
  const floor = office?.floors.find((floor) => floor.id === resource.parent_id);

  const coverImageUrl = getResourceCoverUrl(resource, 300);
  const tagsPopoverDisclosure = useDisclosure();
  const isMobile = useIsMobile();
  const maxLength = hierarchyRight ? 36 : 40;
  const truncatedNames = React.useMemo(() => {
    return truncateOfficeAndFloor(
      office?.name || "",
      floor?.name || "",
      undefined,
      isMobile ? maxLength * 1.5 : maxLength
    );
  }, [office?.name, floor?.name, isMobile, maxLength]);

  if (!resource) return null;

  return (
    <div
      className={cn(classes.Wrapper, className)}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {showPicture && (
        <div
          className={cn(
            imageSize === "sm" &&
              "h-[40px] w-[40px] flex-shrink-0 overflow-hidden rounded-[5px] bg-neutral-50",
            imageSize === "md" &&
              "h-[57px] w-[57px] flex-shrink-0 overflow-hidden rounded-[10px] bg-neutral-50",
            imageSize === "default" && classes.ImageWrapper
          )}
        >
          <Image
            src={
              coverImageUrl ||
              gerCroppedImageUrl(
                "https://cdn.gettactic.com/defaults/room.png",
                imageSize === "sm" ? 40 : imageSize === "md" ? 57 : 300
              )
            }
            className={
              imageSize === "sm"
                ? "h-[40px] object-cover"
                : imageSize === "md"
                  ? "h-[57px] object-cover"
                  : classes.Image
            }
          />
        </div>
      )}
      <div className={classes.InfoSide}>
        <div className={cn(classes.InfoWrapper, infoWrapperClassName)}>
          <div className="flex w-full items-center justify-between">
            <span
              className={cn(
                // A name, not a heading: styled as one, without being one in lists and buttons
                "block",
                headingClasses.h5,
                classes.ResourceName,
                headingClassName
              )}
            >
              {resource.name}
            </span>
            {headerRight}
          </div>
          {showHierarchy && (
            <div className="flex w-full min-w-0 items-center justify-between gap-1 truncate md:max-w-auto">
              <div className={cn(classes.ResourceLocation, hierarchyClassName)}>
                {hierarchyLeft}
                <span className="hidden font-inter font-semiBold md:inline">
                  {truncatedNames.officeName}
                </span>
                <span className="hidden shrink-0 font-inter font-semiBold md:inline">
                  ·
                </span>
                <span className={"font-inter font-semiBold"}>
                  {truncatedNames.floorName}
                </span>
                <CapacityLabel resource={resource} />
              </div>

              {!!hierarchyRight && (
                <div className="shrink-0 font-inter text-[13px] text-content">
                  {hierarchyRight}
                </div>
              )}
            </div>
          )}
          {showAmenities && (
            // A block, not a <p>: the badges are blocks
            <div className={classes.ResourceTagsWrapper}>
              <TooltipProvider>
                {(tagsToShow.length || 0) > 0
                  ? tagsToShow.map((tag) => (
                      <Tooltip key={tag.id}>
                        <TooltipTrigger asChild>
                          <Badge
                            variant={"secondary"}
                            className={
                              "!border !border-neutral-100 inline-block min-w-0 max-w-[100px] truncate bg-transparent px-1.5 py-0.5 font-inter font-normal text-[12px] text-content capitalize"
                            }
                            size={"xsm"}
                          >
                            {tag.name}
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <span className={"text-[13px]"}>{tag.name}</span>
                        </TooltipContent>
                      </Tooltip>
                    ))
                  : null}
              </TooltipProvider>
              {tagsToShow.length < (resource.tags?.length || 0) ? (
                <Popover
                  triggerAsChild
                  open={tagsPopoverDisclosure.isOpen}
                  triggerClassname={"w-fit"}
                  // The list is only read: focus stays on the button, which closes it on blur
                  contentProps={{
                    onOpenAutoFocus: (event) => event.preventDefault(),
                    "aria-label": "Amenities"
                  }}
                  content={
                    <div
                      className={
                        "flex max-w-[300px] flex-wrap justify-center gap-2"
                      }
                    >
                      {(amenities.length || 0) > 0
                        ? amenities.map((tag) => (
                            <Badge
                              variant={"outline"}
                              key={tag.id}
                              size={"xsm"}
                            >
                              {tag.name}
                            </Badge>
                          ))
                        : null}
                    </div>
                  }
                >
                  {/* A button, so the other amenities open from the keyboard too */}
                  <button
                    type="button"
                    aria-label={`${(resource.tags?.length || 0) - tagsToShow.length} more amenities`}
                    onMouseEnter={tagsPopoverDisclosure.onOpen}
                    onMouseLeave={tagsPopoverDisclosure.onClose}
                    onFocus={tagsPopoverDisclosure.onOpen}
                    onBlur={tagsPopoverDisclosure.onClose}
                    className="rounded-full"
                  >
                    <Badge
                      variant={"outline"}
                      key={"plus"}
                      className={"inline-block"}
                      size={"xsm"}
                    >
                      +{(resource.tags?.length || 0) - tagsToShow.length}
                    </Badge>
                  </button>
                </Popover>
              ) : undefined}
            </div>
          )}
          {schedules && schedules.length > 0 && (
            <div
              className={cn(
                "text-[13px] text-black",
                "flex w-full items-center gap-2"
              )}
            >
              {formatStartEndSlotHours(schedules[0], " → ")}
              {schedules.length > 1 && (
                <span className={"text-content"}>
                  + {schedules.length - 1} more
                </span>
              )}
              {showAttenders && (
                <span className={"flex flex-1 justify-end"}>
                  <UserAvatarStack
                    users={getScheduleAttendeeUsers(schedules[0])}
                  />
                </span>
              )}
            </div>
          )}
          {footer}
        </div>
        {allowFavorite && (
          <div
            className={cn(
              isMobile
                ? classes.FavoriteWrapperMobile
                : resource.favorite_id
                  ? classes.FavoriteWrapperActive
                  : classes.FavoriteWrapper
            )}
          >
            <Button
              disabled={isFavoriteLoading}
              variant={"icon"}
              aria-label={`Favorite ${resource.name ?? "resource"}`}
              aria-pressed={!!resource.favorite_id}
              onClick={async (event) => {
                event.stopPropagation();
                event.preventDefault();

                if (resource) await toggleResourceFavorite(resource);
              }}
            >
              {resource.favorite_id ? (
                <StarIcon
                  className={"size-3.5 fill-neutral-800 text-neutral-800"}
                />
              ) : (
                <StarIcon className={"size-3.5"} />
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
